import { debounce } from "perfect-debounce";
import PQueue from "p-queue";
import { ItemType, OrderBy, ItemType as SettingItemType, SettingScopesEnum } from "@simbelapp/order-sdk";
import type { IFindOptions, IHeaderNav, IIndexHeader, IPaginationNav } from "~/utils/interfaces/table-interfaces";
import { useState } from "#imports";
import { useSettingsStore } from "~/store/settings/settings.store";
import { useAuthStore } from "~/store/auth/auth.store";

// Map pour stocker les fonctions de fetch par clé d'instance
const fetchFunctions = new Map<string, () => any>();
const fetchFunctionsOnlyData = new Map<string, () => any>();

export const usePagination = (key?: string) => {
  const LIMIT = ref(50);
  const settingsStore = useSettingsStore();
  const { gridDetails } = storeToRefs(settingsStore);
  const authStore = useAuthStore();

  // Utiliser une clé unique pour cette instance
  const instanceKey = key || "default";

  // Fonction par défaut pour cette instance
  if (!fetchFunctions.has(instanceKey)) {
    fetchFunctions.set(instanceKey, () => {
      console.log(`Function not set for instance: ${instanceKey}`);
    });
  }

  // Fonction pour exécuter la fetchFunction de cette instance
  const executeFetch = async () => {
    const fetchFn = fetchFunctions.get(instanceKey);
    if (fetchFn) {
      await fetchFn();
    }
  };

  const executeFetchOnlyData = async () => {
    const fetchFn = fetchFunctionsOnlyData.get(instanceKey);
    if (fetchFn) {
      await fetchFn();
    }
  };

  function setFetchFunction(f: () => any, limit = LIMIT.value, keepOptions = false) {
    if (!keepOptions) {
      reset();
      paginateOptions.value.limit = limit;
    }
    fetchFunctions.set(instanceKey, f);
  }

  function setFetchFunctionOnlyData(f: () => any, limit = LIMIT.value, keepOptions = false) {
    if (!keepOptions) {
      reset();
      paginateOptions.value.limit = limit;
    }
    fetchFunctionsOnlyData.set(instanceKey, f);
  }

  const paginateOptions = useState<IFindOptions>(instanceKey, () => {
    return {
      offset: 0,
      limit: LIMIT.value,
      search: null,
      filter: null,
      sortingColumn: null,
      order: null,
    };
  });

  function reset() {
    paginateOptions.value.offset = 0;
    // eslint-disable-next-line
    paginateOptions.value.limit = paginateOptions.value.limit;
    paginateOptions.value.search = null;
    paginateOptions.value.filter = null;
    paginateOptions.value.sortingColumn = null;
    paginateOptions.value.order = null;
  }

  function resetOffset() {
    paginateOptions.value.offset = 0;
  }

  async function fetchData() {
    await executeFetch();
  }

  const queue = new PQueue({ concurrency: 1 });
  const pageNavigate = debounce((val: IPaginationNav, onlyData?: boolean) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.limit = val.limit ?? 50;
      paginateOptions.value.offset = val.offset;
      gridDetails.value.paginationValues[0] = {
        limit: val.limit ?? 50,
        offset: val.offset ?? 0,
      };
      if (authStore.user?.userId) {
        await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
      }
      if (!onlyData) {
        await executeFetch();
      } else {
        await executeFetchOnlyData();
      }
    });
  }, 200);

  const pageNavigateWithoutSettings = debounce((val: IPaginationNav) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.limit = val.limit ?? 50;
      paginateOptions.value.offset = val.offset;

      await executeFetch();
    });
  }, 200);

  const sortColumn = debounce((val: IIndexHeader, onlyData?: boolean) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.sortingColumn = val.key;
      paginateOptions.value.order = gridDetails.value.sortingValues[0]?.order ?? val.sortOrder;
      if (val.key && gridDetails.value.sortingValues[0]?.order) {
        gridDetails.value.sortingValues[0] = {
          sortingColumn: val.key,
          scope: val.scope as SettingScopesEnum,
          order: gridDetails.value.sortingValues[0].order as OrderBy,
        };
        if (authStore.user?.userId) {
          await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
        }
      }
      if (!onlyData) {
        await executeFetch();
      } else {
        await executeFetchOnlyData();
      }
    });
  }, 200);

  const sortColumnWithoutSettings = debounce((val: IIndexHeader, onlyData?: boolean) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.sortingColumn = val.key;
      paginateOptions.value.order = val.sortOrder;

      if (!onlyData) {
        await executeFetch();
      } else {
        await executeFetchOnlyData();
      }
    });
  }, 200);

  const tabNavigate = debounce((val: IHeaderNav, onlyData?: boolean) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.offset = 0;
      paginateOptions.value.filter = val.filter;
      gridDetails.value.paginationValues[0] = {
        limit: paginateOptions.value.limit ?? 50,
        offset: 0,
      };
      if (val?.filter) {
        gridDetails.value.sectionValues[0] = val.type
          ? {
              sectionName: val.filter,
              itemType: val.type as ItemType,
            }
          : {
              sectionName: val.filter,
            };
        if (authStore.user?.userId) {
          gridDetails.value.paginationValues[0] = {
            limit: paginateOptions.value.limit ?? 50,
            offset: 0,
          };
          await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
        }
      }
      if (!onlyData) {
        await executeFetch();
      } else {
        await executeFetchOnlyData();
      }
    });
  }, 200);

  const tabNavigateWithoutSettings = debounce((val: IHeaderNav, onlyData?: boolean) => {
    queue.clear();
    queue.add(async () => {
      paginateOptions.value.offset = 0;
      paginateOptions.value.filter = val.filter;

      if (!onlyData) {
        await executeFetch();
      } else {
        await executeFetchOnlyData();
      }
    });
  }, 200);

  const searchNavigate = debounce(
    (val: string, onlyData?: boolean) => {
      queue.clear();
      queue.add(async () => {
        paginateOptions.value.search = val;
        paginateOptions.value.offset = 0;
        gridDetails.value.searchValues[0] = {
          stringToSearch: val,
        };
        if (authStore.user?.userId) {
          await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
        }
        if (!onlyData) {
          await executeFetch();
        } else {
          await executeFetchOnlyData();
        }
      });
    },
    300,
    { trailing: false },
  );

  const searchNavigateWithoutSettings = debounce(
    (val: string, onlyData?: boolean) => {
      queue.clear();
      queue.add(async () => {
        paginateOptions.value.search = val;
        paginateOptions.value.offset = 0;

        if (!onlyData) {
          await executeFetch();
        } else {
          await executeFetchOnlyData();
        }
      });
    },
    300,
    { trailing: false },
  );

  function getFindOptions(options: IFindOptions) {
    const obj: Record<string, any> = {};
    Object.entries(options).forEach(([key, val]) => {
      if (val) {
        obj[key] = val;
      }
    });
    return obj;
  }

  function getPaginateOpts() {
    paginateOptions.value.offset = gridDetails.value.paginationValues[0]?.offset ?? 0;
    paginateOptions.value.limit = gridDetails.value.paginationValues[0]?.limit ?? 50;
    paginateOptions.value.search = gridDetails.value.searchValues[0]?.stringToSearch ?? null;
    paginateOptions.value.filter = gridDetails.value.sectionValues[0]?.sectionName ?? null;
    paginateOptions.value.sortingColumn = gridDetails.value.sortingValues[0]?.sortingColumn ?? null;
    paginateOptions.value.order = gridDetails.value.sortingValues[0]?.order ?? null;
  }

  return {
    paginateOptions,
    setFetchFunction,
    setFetchFunctionOnlyData,
    sortColumn,
    pageNavigate,
    pageNavigateWithoutSettings,
    tabNavigateWithoutSettings,
    sortColumnWithoutSettings,
    tabNavigate,
    fetchData,
    searchNavigate,
    searchNavigateWithoutSettings,
    getFindOptions,
    reset,
    resetOffset,
    getPaginateOpts,
  };
};
